<template>
    <div class="form-area col-12 col-lg-auto">
        <collapsible :is-open="true" :title="$t('validation.form.parameters')">
            <suspense>
                <template #default>
                    <pk-parameters @pk-parameters-loaded="reportStore.pkParameters = $event" />
                </template>

                <template #fallback>
                    <div class="pk-spinner-container">
                        <spinner />
                    </div>
                </template>
            </suspense>
        </collapsible>

        <collapsible :is-open="true" :title="$t('validation.form.validation')">
            <div class="form-group">
                <span class="label text-right">{{ $t('validation.form.dosage') }}</span>
                <div>
                    <div v-for="adjustment in requestDataStore.selectedAdjustmentDosages" :key="adjustment">
                        {{ adjustment.getDescriptionString() }}
                    </div>
                </div>
            </div>

            <div class="form-group">
                <label for="nextControlDate" class="text-right">{{ $t('validation.form.nextControl') }}</label>
                <date-picker
                    v-model="reportStore.nextControlDate"
                    :with-time="true"
                    :required="true"
                />
            </div>

            <div class="form-group align-top">
                <label for="expectedness" class="text-right">{{ $t('validation.form.expectedness') }}</label>
                <textarea id="expectedness" v-model="reportStore.expectedness"></textarea>
            </div>

            <div class="form-group align-top">
                <label for="suitability" class="text-right">{{ $t('validation.form.suitability') }}</label>
                <textarea id="suitability" v-model="reportStore.suitability"></textarea>
            </div>

            <div class="form-group align-top">
                <label for="prediction" class="text-right">{{ $t('validation.form.prediction') }}</label>
                <textarea id="prediction" v-model="reportStore.prediction"></textarea>
            </div>

            <div class="form-group align-top">
                <label for="remonitoring" class="text-right">{{ $t('validation.form.remonitoring') }}</label>
                <textarea id="remonitoring" v-model="reportStore.remonitoring"></textarea>
            </div>

            <div class="form-group align-top">
                <label for="warning" class="text-right">{{ $t('validation.form.warning') }}</label>
                <textarea id="warning" v-model="reportStore.warning"></textarea>
            </div>
        </collapsible>

        <div class="padding-container">
            <button type="button" id="validateButton" class="btn flex" @click="handleValidate" :disabled="validateButtonDisabled">
                <span>{{ $t('action.validate') }}</span>
                <spinner v-if="validateButtonDisabled" />
            </button>
        </div>
    </div>

    <div class="data-area col-12 col-lg">
        <!-- Modal to edit chart settings -->
        <chart-settings-modal
            :graph-settings-title="$t('chartSettings.groupName.validationSettings')"
            v-model:is-open="isSettingsModalOpen"
            v-model:graph-settings="chartStore.validationSettings"
        />

        <concentration-chart
            :graph-settings="chartStore.validationSettings"
            @open-settings="isSettingsModalOpen = true"
        />
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeMount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Collapsible from '@/components/Collapsible.vue';
import ChartSettingsModal from '@/components/ChartSettingsModal.vue';
import ConcentrationChart from '@/components/ConcentrationChart.vue';
import DatePicker from '@/components/DatePicker.vue';
import Spinner from '@/components/Spinner.vue';
import { useChartsStore } from '@/store/charts';
import { useRequestDataStore } from '@/store/request-data';
import { useReportStore } from '@/store/report';
import { PredictionTraits } from '@/service/models/request/computation/computing-traits/prediction-traits';
import { DatesRange } from '@/utilities/types/dates-range';
import { Duration } from '@/utilities/types/duration';
import { ComputingOption } from '@/service/models/request/computation/computing-option';
import { ComputationResult } from '@/service/models/response/computation-result';
import { DataPrediction } from '@/service/models/response/computation/data-responses/data-prediction';
import PkParameters from './PkParameters.vue';

export default defineComponent({
    components: {
        ChartSettingsModal,
        Collapsible,
        ConcentrationChart,
        DatePicker,
        PkParameters,
        Spinner,
    },

    setup()
    {
        const chartStore = useChartsStore();
        const requestDataStore = useRequestDataStore();
        const reportStore = useReportStore();
        const router = useRouter();

        const isSettingsModalOpen = ref(false);

        const validateButtonDisabled = computed(() => {
            return (
                reportStore.pkParameters.parameters.size === 0 ||
                reportStore.steadyStateStats === null
            );
        });

        // Default value for next control date
        if (reportStore.nextControlDate === null) {
            reportStore.nextControlDate = new Date();
            reportStore.nextControlDate.setMonth(reportStore.nextControlDate.getMonth() + 1);
        }

        function handleValidate()
        {
            reportStore.validationDate = new Date();

            router.push("/app/reports");
        }

        onBeforeMount(() => {
            // The PkParameters component is going to request again for PK parameters, so we can clear the old ones
            // from the report.
            reportStore.pkParameters.parameters.clear();
        });

        // Load statistics object for the selected adjustment
        onMounted(async () => {
            // There is currently no way to tell when the steady state will be reached using the API, so
            // let's take the latest date we can, which is the last cycle before the end of the treatment
            const lastDosage = requestDataStore.lastDosageInSelectedAdjustment;
            if (!lastDosage) {
                throw new Error("No dosage found in the selected adjustment");
            }

            const steadyStateMoment = new DatesRange(
                lastDosage.getPreviousDoseDateFrom(Duration.fromSeconds(1).subFrom(lastDosage.end)),
                lastDosage.end
            );

            // Prepare computation request
            const computationRequest = requestDataStore.makeComputationRequest(new PredictionTraits(
                steadyStateMoment,
                20,
                new ComputingOption(
                    requestDataStore.computationParameterTypeForAdjustmentRequest,
                    ComputingOption.CompartmentOption.ALL_ACTIVE_MOIETIES,
                    true,
                    false,
                    false
                )
            ));

            // Run API query
            const query = requestDataStore.makeComputationQuery([computationRequest], true);
            const result = await requestDataStore.makeApiRequest(query).load() as ComputationResult;
            const response = result.getResponseById(computationRequest.requestId);

            if (!(response.data instanceof DataPrediction)) {
                throw Error("Adjustment statistics response received from API is not a <dataPrediction>");
            }

            // Store the statistics response in the store
            reportStore.steadyStateStats = response.data.cycleData[0].statistics;
        });

        return {
            chartStore,
            requestDataStore,
            validateButtonDisabled,
            reportStore,
            isSettingsModalOpen,
            handleValidate,
        };
    },
});
</script>

<style lang="scss" scoped>

.pk-spinner-container {
    font-size: 2rem;
}

.padding-container {
    padding: 1rem;
}

#validateButton {
    justify-content: center;
    width: 100%;
}

</style>
